import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { usePostDocumentsMutation } from "src/core/api/documents";
import { MappedStakeholder } from "src/core/constants/folder";
import { useFolder, useFolderCorporationId } from "src/core/contexts/FolderContext";
import { useFolderGraphSync } from "src/core/contexts/FolderGraphSyncContext";
import { computeStakeholderIncompleteFields } from "src/core/forms/folder/folder.incomplete";
import {
  defaultUpdateStakeholderFullValues,
  UpdateStakeholderFullFormValues,
  updateStakeholderFullSchema,
} from "src/core/forms/stakeholder/updateStakeholderFull.schema";
import {
  useCreateOrReplaceExternalDocumentMutation,
  useDeleteDocumentMutation,
  useDeleteExternalDocumentMutation,
  useUpdateDocumentMutation,
  useUpdateStakeholderMutation,
} from "src/core/gql/graphql";
import { useRequiredDocuments } from "src/core/hooks/common/useRequiredDocuments";
import { useEReputation } from "src/core/hooks/folder/useEReputation";
import { useUploadDocumentsErrorToast } from "src/core/hooks/folder/useUploadDocumentsErrorToast";
import { useToast } from "src/core/hooks/use-toast";
import { getDefaultDocuments } from "src/core/mappers/document.mapper";
import { mapFormValuesToMappedStakeholder } from "src/core/mappers/stakeholder.mapper";
import { deleteDocuments, uploadDocuments } from "src/core/services/document.service";
import { Document } from "src/core/types/document";
import { toIsoUtcFromDate } from "src/core/utils/date";
import { getDocumentsExpirationUpdates } from "src/core/utils/documents/getDocumentsExpirationUpdates";
import { buildUpdateStakeholderVariables } from "src/core/utils/stakeholders";

type UseUpdateStakeholderFullFormProps = {
  /** Stakeholder à éditer. */
  selectedStakeholderForEdit: MappedStakeholder;

  /** Définit le stakeholder sélectionné. Passer `undefined` pour revenir à la liste. */
  setSelectedStakeholderForEdit: (stakeholder?: MappedStakeholder) => void;

  /** Callback appelé lorsque le formulaire est soumis avec succès. Permet de déclencher un refetch de la liste des stakeholders. */
  onSuccess?: () => void;
};

/**
 * Hook pour le formulaire de mise à jour d'un stakeholder (partie prenante).
 * A la différence du formulaire de création, il y a tous les champs.
 */
export function useUpdateStakeholderFullForm({
  selectedStakeholderForEdit,
  setSelectedStakeholderForEdit,
  onSuccess,
}: UseUpdateStakeholderFullFormProps) {
  const { t } = useTranslation();
  const { toast } = useToast();

  const { isFromBasicForm } = useFolderGraphSync();

  // Permet de récupérer l'id de la société du dossier
  const corporationId = useFolderCorporationId();

  // Permet d'appeler la mutation de création d'un stakeholder
  const [updateStakeholder] = useUpdateStakeholderMutation();
  const [postDocuments] = usePostDocumentsMutation();
  const [createOrReplaceExternalDocument] = useCreateOrReplaceExternalDocumentMutation();
  const [deleteDocument] = useDeleteDocumentMutation();
  const [deleteExternalDocument] = useDeleteExternalDocumentMutation();
  const [updateDocument] = useUpdateDocumentMutation();

  // Permet de gérer le spinner au niveau du bouton de soumission
  const [isLoading, setIsLoading] = useState(false);
  const { folderFile, isReadOnly } = useFolder();
  const [defaultDocuments, setDefaultDocuments] = useState<Record<string, Document>>({});

  const requiredDocuments = useRequiredDocuments(folderFile, selectedStakeholderForEdit.id);
  const { handleUploadErrors } = useUploadDocumentsErrorToast({ requiredDocuments, toast });

  const { callSafely: callEReputationSafely } = useEReputation();

  // Liste des champs requis pour la validation du dossier, mais pas nécessaire pour l'enregistrement.
  // On les affiche dans un état différent des champs requis pour l'enregistrement.
  const [incompleteFields, setIncompleteFields] = useState<Set<string>>(new Set());

  // Formulaire de création d'un stakeholder
  const form = useForm<UpdateStakeholderFullFormValues>({
    resolver: zodResolver(updateStakeholderFullSchema()),
    defaultValues: defaultUpdateStakeholderFullValues(selectedStakeholderForEdit),
  });

  useEffect(() => {
    if (!selectedStakeholderForEdit.id) return;

    const defaultDoc = getDefaultDocuments(
      folderFile,
      selectedStakeholderForEdit.id,
      requiredDocuments
    );
    setDefaultDocuments(defaultDoc);
    form.reset({
      ...defaultUpdateStakeholderFullValues(selectedStakeholderForEdit),
      documents: defaultDoc,
    });

    if (!isFromBasicForm) {
      setIncompleteFields(computeStakeholderIncompleteFields(form.getValues()));
    }
  }, [
    requiredDocuments,
    folderFile,
    corporationId,
    form,
    selectedStakeholderForEdit,
    isFromBasicForm,
  ]);

  const onSubmit = useCallback(
    async (data: UpdateStakeholderFullFormValues) => {
      if (!folderFile.folderFile?.id) {
        return;
      }

      try {
        setIsLoading(true);

        if (!corporationId) {
          throw new Error("Aucun id de société trouvé");
        }

        const variables = buildUpdateStakeholderVariables(
          corporationId,
          selectedStakeholderForEdit,
          data
        );

        // Détecter les documents dont l'expirationDate a changé
        const documentsToUpdate = getDocumentsExpirationUpdates(data.documents, defaultDocuments);

        // 1) On lance les opérations “métier” (hors upload)
        await Promise.all([
          updateStakeholder({ variables }),
          deleteDocuments(data.documents, defaultDocuments, deleteDocument, deleteExternalDocument),
          ...documentsToUpdate.map(({ fileId, expirationDate }) =>
            updateDocument({
              variables: {
                id: fileId,
                expirationDate: expirationDate
                  ? toIsoUtcFromDate(expirationDate, "yyyy-MM-dd")
                  : undefined,
              },
            })
          ),
        ]);

        // 2) Upload des documents (résultats par document)
        const uploadResults = await uploadDocuments(
          data.documents,
          selectedStakeholderForEdit.id,
          postDocuments,
          createOrReplaceExternalDocument
        );

        const failedUploads = uploadResults.filter((r) => !r.ok);

        // Affichage d'un toast d'erreur si des documents n'ont pas pu être uploadés
        handleUploadErrors(failedUploads);

        // 3) Appel à eReputation pour que le screening se fasse après la mise à jour du stakeholder.
        await callEReputationSafely({
          folderFileId: folderFile.folderFile.id,
          logContext: "useUpdateStakeholderFullForm::onSubmit",
        });

        // Réinitialiser le formulaire avec les nouvelles données afin de recalculer le nombre de champs manquants.
        form.reset(data);

        const updatedMapped = mapFormValuesToMappedStakeholder(
          selectedStakeholderForEdit.id,
          data,
          selectedStakeholderForEdit
        );
        setSelectedStakeholderForEdit(updatedMapped);

        toast({ description: t("form.addStakeholderFull.success") });

        onSuccess?.();

        // Gestion du cas des champs requis pour la validation du dossier, mais pas nécessaire pour l'enregistrement.
        setIncompleteFields(computeStakeholderIncompleteFields(data));
      } catch (error) {
        console.error("useUpdateStakeholderFullForm::error", error);
        toast({ description: t("form.addStakeholderFull.fail"), variant: "destructive" });
      } finally {
        setIsLoading(false);
      }
    },
    [
      toast,
      t,
      corporationId,
      updateStakeholder,
      form,
      setSelectedStakeholderForEdit,
      onSuccess,
      selectedStakeholderForEdit,
      postDocuments,
      folderFile,
      defaultDocuments,
      deleteDocument,
      deleteExternalDocument,
      createOrReplaceExternalDocument,
      updateDocument,
      handleUploadErrors,
      callEReputationSafely,
    ]
  );

  return {
    form,
    onSubmit,
    isLoading,
    requiredDocuments,
    isReadOnly,
    incompleteFields,
  };
}
